import React, { useEffect, useState } from 'react';
import { CalendarSync, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { useT } from '../../contexts/LocaleProvider';
import api from '../../utils/api';

/**
 * Compact widget that shows the user's Google Calendar connection state,
 * starts the OAuth flow when disconnected, and triggers a sync of the
 * events currently visible to them. Sits in the Calendar page right rail.
 */
export default function GoogleCalendarConnect() {
  const t = useT();
  const [status, setStatus] = useState(null); // null = loading
  const [busy, setBusy] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.googleCalendarStatus()
      .then(setStatus)
      .catch(() => setStatus({ connected: false }));
  }, []);

  const connect = async () => {
    setBusy(true); setError('');
    try {
      const { oauth_url } = await api.googleCalendarOAuthUrl();
      window.location.href = oauth_url;
    } catch (e) {
      setError(e.response?.data?.detail || t('gcal.connectFailed'));
      setBusy(false);
    }
  };

  const sync = async () => {
    setBusy(true); setError('');
    try {
      const result = await api.googleCalendarSync();
      setLastSync(result);
    } catch (e) {
      setError(e.response?.data?.detail || t('gcal.syncFailed'));
    } finally {
      setBusy(false);
    }
  };

  if (status === null) return null; // loading · don't flash

  return (
    <div className="bg-[hsl(var(--surface))] border border-border rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <CalendarSync className="h-3.5 w-3.5 text-[hsl(var(--text-muted))]" />
        <h2 className="text-[14px] font-semibold">{t('gcal.title')}</h2>
      </div>
      <div className="px-4 py-3.5 space-y-3">
        {error && (
          <div className="border border-[hsl(var(--danger)/.3)] bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] px-2.5 py-1.5 text-[12px] rounded-md">
            {error}
          </div>
        )}

        {!status.connected && (
          <>
            <p className="text-[12.5px] text-[hsl(var(--text-soft))] leading-snug">
              {t('gcal.intro')}
            </p>
            <Button size="sm" onClick={connect} disabled={busy} className="w-full gap-1.5">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarSync className="h-3.5 w-3.5" />}
              {t('gcal.connect')}
            </Button>
          </>
        )}

        {status.connected && (
          <>
            <div className="flex items-center gap-2 text-[12.5px]">
              <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--success))] shrink-0" />
              <span className="font-num truncate text-[hsl(var(--text-soft))]">{status.google_email}</span>
            </div>
            <Button size="sm" variant="outline" onClick={sync} disabled={busy} className="w-full gap-1.5">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarSync className="h-3.5 w-3.5" />}
              {busy ? t('gcal.syncing') : t('gcal.syncNow')}
            </Button>
            {lastSync && (
              <p className="text-[11.5px] text-[hsl(var(--text-muted))] text-center">
                {t('gcal.lastSync', { n: lastSync.synced_events })}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
